import { useMemo } from "react";

import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePrompt, useParseInputVariables } from "@/queries";
import { PromptBlueprint } from "@/types";
import { InlineTemplate } from "@/types/evaluate";

interface PromptTemplateConfig {
  template?: {
    id?: number;
    name?: string;
    version_number?: number | null;
    label?: string;
  };
  inline_template?: InlineTemplate;
}

export const usePromptTemplateInputVariables = (promptConfig: PromptTemplateConfig | undefined) => {
  const auth = useAuth();
  const userToken = auth?.userToken || "";
  const user = useUser();
  const activeWorkspaceId = user?.activeWorkspaceId || 0;

  // Determine if we're in inline mode
  const isInlineMode = !!promptConfig?.inline_template?.prompt_template;

  const promptQuery = usePrompt(userToken, {
    workspace_id: activeWorkspaceId,
    prompt_name: isInlineMode ? undefined : promptConfig?.template?.name,
    number: isInlineMode ? undefined : (promptConfig?.template?.version_number ?? undefined),
    label: isInlineMode ? undefined : promptConfig?.template?.label,
  });

  const selectedPromptTemplateVersion = promptQuery.data;

  // For inline templates, construct a blueprint-like object for parsing
  const blueprintForParsing = useMemo(() => {
    if (isInlineMode && promptConfig?.inline_template) {
      return {
        prompt_template: promptConfig.inline_template.prompt_template,
        metadata: promptConfig.inline_template.metadata,
      } as PromptBlueprint;
    }
    return selectedPromptTemplateVersion as PromptBlueprint;
  }, [isInlineMode, promptConfig?.inline_template, selectedPromptTemplateVersion]);

  const parseInputVariables = useParseInputVariables(userToken, blueprintForParsing);

  const inputVariables = useMemo(
    () => (parseInputVariables.data?.success ? parseInputVariables.data.input_variables : []),
    [parseInputVariables.data],
  );

  return {
    promptQuery,
    selectedPromptTemplateVersion,
    parseInputVariables,
    inputVariables,
    isLoading: parseInputVariables.isLoading,
    error: isInlineMode
      ? parseInputVariables.error
      : promptQuery.error || parseInputVariables.error,
    isInlineMode,
  };
};
